/**
 * Confirmation that a save landed, or the reason it did not. A toast is for what happened in the
 * background; anything the person must act on stays on the page beside the thing it is about.
 */
import { createContext, use, useCallback, useState, type ReactNode } from "react";

import { Icon } from "./Icon";

interface Toast {
  id: number;
  text: string;
  error: boolean;
}

const ToastContext = createContext<(text: string, opts?: { error?: boolean }) => void>(
  () => undefined,
);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, opts: { error?: boolean } = {}) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t.slice(-2), { id, text, error: Boolean(opts.error) }]);
    window.setTimeout(
      () => setToasts((t) => t.filter((x) => x.id !== id)),
      opts.error ? 7000 : 3500,
    );
  }, []);
  return (
    <ToastContext value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.error ? " is-error" : ""}`}>
            <Icon name={t.error ? "alert" : "check"} />
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </ToastContext>
  );
}

export function useToast() {
  return use(ToastContext);
}
